<template>
    <WidgetSection
        ref="WidgetSection"
        :loaded="loaded"
        v-if="!hide"
        :defaultToggle="defaultToggle"
        @toggle="handleToggle"
        :cssClass="`slider-type-1 ${cssClassWidgetSection}`"
        :paywall="paywall"
    >
        <template v-slot:section-title>
            <div class="title">
                {{ $t('components.prospect_reviews.name')}}
            </div>
        </template>

        <template v-slot:section-actions>
            <div class="swiper-navigation">
                <div :class="`swiper-button-prev-${uniqueId}`"></div>
                <div :class="`swiper-button-next-${uniqueId}`"></div>
            </div>
        </template>

        <template v-slot:section-content>
            <div class="row row-slider">
                <swiper-container ref="swiper" class="swiper-container" init="false">
                    <swiper-slide v-for="(review, index) in reviews" :key="index">
                        <component :is="review.url ? 'a' : 'div'" :href="review.url || undefined" target="_blank" rel="noreferrer external" class="slide-container">
                            <StarsReview v-if="review.rating" :score="review.rating" :fullWidth="true" />
                            <div v-if="review.date" class="review-date">{{formatDateReverse(review.date)}}</div>
                            <div class="review-content">
                                <div class="description">
                                    {{ review.text }}
                                </div>
                            </div>
                            <div class="bar">
                                <div class="left">
                                    <ContactImage 
                                        :imgUrl="review.user_img"
                                        :name_first="review.name"
                                    />
                                </div>
                                <div class="right">
                                    <div class="name-review" v-if="review.name">{{ review.name }}</div>
                                    <div class="source" v-if="review.source">{{$t('common.source')}}: {{ review.source }}</div>
                                </div>
                            </div>
                        </component>
                    </swiper-slide>
                </swiper-container>
            </div>
        </template>
    </WidgetSection>
</template>

<script>
    import { useProspectStore } from '@/stores/prospect';
    import WidgetSection from '@/components/sections/WidgetSection';
    import ContactImage from "@/components/elements/misc/ContactImage";
    import StarsReview from '@/components/elements/misc/StarsReview';
    import { formatDateReverse } from '@/utils/formatting';

    export default {
        name: "ProspectApps",
        components: {
            WidgetSection,
            ContactImage,
            StarsReview
        },
        props: {
            defaultToggle: {
                type: Boolean,
                default: false
            },
            cssClassWidgetSection: {
                type: String,
                required: false
            },
            narrowVersion: {
                type: Boolean,
                default: false
            },
            paywall: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                loaded: true,
                uniqueId: Math.random().toString(36).substr(2, 9),
                timeoutId: null,
                isActive: true
            }
        },
        computed: {
            reviews() {
                return useProspectStore().reviewsDetails;
            },
            hide() {
                return !this.reviews || this.reviews.length < 1;
            }
        },
        methods: {
            formatDateReverse,
            initSwiper() {
                if(!this.$refs.swiper) { return; }
                if(!this.isActive) { return; }
                
                const swiperEl = this.$refs.swiper;
                const swiperParams = {
                    slidesPerView: 1,
                    spaceBetween: 24,
                    navigation: {
                        nextEl: `.swiper-button-next-${this.uniqueId}`,
                        prevEl: `.swiper-button-prev-${this.uniqueId}`
                    }
                };

                swiperParams.breakpoints =  this.narrowVersion ?
                                            {
                                                769: {
                                                    slidesPerView: 1.2,
                                                    slidesPerGroup: 1.2
                                                },
                                                1240: {
                                                    slidesPerView: 2,
                                                    slidesPerGroup: 2
                                                }
                                            } : {
                                                769: {
                                                    slidesPerView: 2,
                                                    slidesPerGroup: 2
                                                },
                                                992: {
                                                    slidesPerView: 3,
                                                    slidesPerGroup: 3
                                                }
                                            };
                const existing = swiperEl.swiper;
                if (existing && !existing.destroyed) {
                    existing.update();
                    existing.navigation?.update();
                    return;
                }

                if (existing && existing.destroyed) {
                    swiperEl.swiper = undefined;
                    swiperEl.initialized = false;
                }

                Object.assign(swiperEl, swiperParams);
                swiperEl.initialize();
            },
            handleToggle(isVisible) {
                const swiperEl = this.$refs.swiper;
                if (isVisible) {
                    this.$nextTick(() => {
                        this.initSwiper(); // Re-initialize Swiper after the DOM updates
                    });
                } else if (swiperEl?.swiper && !swiperEl.swiper.destroyed) {
                    swiperEl.swiper.destroy(true, true);
                }
            }
        },
        mounted() {
            if(this.defaultToggle) {
                this.timeoutId = setTimeout(() => {
                    this.initSwiper();
                }, 300);
            }
        },
        beforeUnmount() {
            this.isActive = false;

            if (this.$refs.swiper?.swiper && !this.$refs.swiper.swiper.destroyed) {
                this.$refs.swiper.swiper.destroy(true, true);
            }

            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
        },
    }
</script>